/* eslint-disable react-hooks/exhaustive-deps */

import { Heading3 } from '@blueprint/heading';
import { Text } from '@blueprint/text';
import { format, isBefore, setHours } from 'date-fns';
import Maybe from 'graphql/tsutils/Maybe';
import React, { useEffect, useState } from 'react';
import {
    getAvailabilityWindows,
    getDaysForAvailibility
} from './EmailAgentAvailabilityForm.helpers';
import {
    AvailabilityCheckbox,
    AvailabilityTable,
    SubTitle
} from './EmailAgentAvailabilityForm.styles';
import { AvailableDayProps } from './EmailAgentAvailabilityForm.types';

type EmailAgentAvailabilityFormProps = {
    startDate: Date;
    selectedAvailabilities: Maybe<Array<string>>;
    onChange: (timeWindows: Array<string>) => void;
};

const getAvailabilityProps = (field: string) => ({ id: field, name: field });

const formatTimeWindow = ({
    day,
    open,
    close
}: {
    day: Date;
    open: number;
    close: number;
}) => {
    const formattedDay = format(day, 'yyyy-MM-dd');
    const formattedOpen = format(setHours(day, open), 'HH:mm');
    const formattedClose = format(setHours(day, close), 'HH:mm');
    return `${formattedDay} ${formattedOpen} - ${formattedClose}`;
};

const AvailableDay = ({ data }: AvailableDayProps) => {
    const dayOfWeek = format(data, 'EEE');
    const dayOfMonth = format(data, 'do');
    return (
        <time dateTime={format(data, 'yyyy-MM-dd')}>
            <Text isBold>{dayOfWeek}</Text>
            <Text>{dayOfMonth}</Text>
        </time>
    );
};

const EmailAgentAvailabilityForm = ({
    startDate,
    selectedAvailabilities,
    onChange
}: EmailAgentAvailabilityFormProps) => {
    const [selectedWindows, setSelectedWindows] = useState<Array<string>>(
        selectedAvailabilities || []
    );
    const availableDays = getDaysForAvailibility(startDate);
    const availabilityWindows = getAvailabilityWindows();

    useEffect(() => {
        onChange(selectedWindows);
    }, [selectedWindows]);

    const timeCheckboxChanged = (timeWindow: string) => {
        const timeIndexInList = selectedWindows.findIndex(
            (listWindow) => listWindow === timeWindow
        );
        if (timeIndexInList === -1) {
            setSelectedWindows(selectedWindows.concat(timeWindow));
        } else {
            setSelectedWindows(
                selectedWindows.filter((_, index) => index !== timeIndexInList)
            );
        }
    };

    return (
        <>
            <Heading3 size="7">When are you available?</Heading3>
            <SubTitle>
                Suggest some times when you would be available to view the
                property.
            </SubTitle>

            <AvailabilityTable data-testid="email-agent-availability-table">
                <thead>
                    <tr>
                        <th className="dayOfWeek">Day</th>
                        <th>
                            <Text isBold>Morning</Text>
                        </th>
                        <th>
                            <Text isBold>Afternoon</Text>
                        </th>
                        <th>
                            <Text isBold>Evening</Text>
                        </th>
                    </tr>
                </thead>

                <tbody>
                    {availableDays.map((day) => (
                        <tr key={day.toISOString()}>
                            <td>
                                <AvailableDay data={day} />
                            </td>

                            {availabilityWindows.map(({ open, close }) => {
                                const edgeOfWindow = setHours(day, close);
                                const checkboxIsDisabled = isBefore(
                                    edgeOfWindow,
                                    new Date()
                                );
                                const timeWindowValue = formatTimeWindow({
                                    day,
                                    open,
                                    close
                                });

                                return (
                                    <td
                                        key={timeWindowValue}
                                        className="availability"
                                    >
                                        <AvailabilityCheckbox
                                            hasBorder
                                            checked={
                                                selectedWindows.indexOf(
                                                    timeWindowValue
                                                ) !== -1
                                            }
                                            label={timeWindowValue}
                                            value={timeWindowValue}
                                            disabled={checkboxIsDisabled}
                                            onChange={() =>
                                                timeCheckboxChanged(
                                                    timeWindowValue
                                                )
                                            }
                                            {...getAvailabilityProps(
                                                timeWindowValue
                                            )}
                                        />
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </AvailabilityTable>
        </>
    );
};

export default EmailAgentAvailabilityForm;
